
import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ShoppingBag, Menu, X, Settings } from 'lucide-react';

interface HeaderProps {
  cartCount: number;
  onCartClick: () => void;
}

const Header: React.FC<HeaderProps> = ({ cartCount, onCartClick }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Collections', path: '/shop' },
    { name: 'Notre Histoire', path: '/' },
  ];

  const isHome = location.pathname === '/';
  const isAdminPage = location.pathname === '/admin' || location.pathname === '/login';

  const textColorClass = isAdminPage 
    ? 'text-brand-black' 
    : isHome && !isScrolled 
      ? 'text-white' 
      : 'text-brand-black';

  return (
    <header className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${
      isScrolled || !isHome ? 'bg-brand-offwhite py-4 shadow-sm' : 'bg-transparent py-8'
    }`}>
      <div className="container mx-auto px-6 flex items-center justify-between">
        
        {/* Left Section: Navigation (Alignée à l'extrême gauche) */}
        <div className="flex-1 hidden md:flex items-center justify-start space-x-12">
          {navLinks.map((link) => (
            <Link 
              key={link.name} 
              to={link.path}
              className={`text-[9px] uppercase tracking-[0.3em] font-bold hover:text-brand-gold luxury-transition whitespace-nowrap ${textColorClass}`}
            >
              {link.name}
            </Link>
          ))}
        </div>

        {/* Mobile Toggle (Visible uniquement sur mobile) */}
        <div className="md:hidden flex-1">
          <button 
            className={`${textColorClass}`}
            onClick={() => setIsMobileMenuOpen(true)}
          >
            <Menu size={24} />
          </button>
        </div>

        {/* Center Section: Brand Logo (Espace de respiration garanti) */}
        <div className="flex-shrink-0 px-8">
          <Link to="/" className="text-center">
            <h1 className={`font-serif text-xl sm:text-2xl md:text-3xl lg:text-4xl tracking-tighter font-bold luxury-transition whitespace-nowrap ${textColorClass}`}>
              KHAMAL BAGS
            </h1>
          </Link>
        </div>

        {/* Right Section: Icons (Alignée à l'extrême droite) */}
        <div className="flex-1 flex items-center justify-end space-x-6 md:space-x-10">
          <Link 
            to="/admin" 
            title="Espace Gestion"
            className={`opacity-40 hover:opacity-100 luxury-transition ${textColorClass}`}
          >
            <Settings size={18} />
          </Link>
          <button 
            onClick={onCartClick}
            className={`relative p-2 group ${textColorClass}`}
          >
            <ShoppingBag size={22} className="group-hover:text-brand-gold transition-colors" />
            {cartCount > 0 && (
              <span className="absolute top-0 right-0 bg-brand-gold text-white text-[9px] w-4 h-4 flex items-center justify-center rounded-full font-bold border-2 border-white">
                {cartCount}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Mobile Sidebar Menu */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 bg-brand-offwhite z-[60] flex flex-col p-8 md:hidden">
          <div className="flex justify-end mb-12">
            <button onClick={() => setIsMobileMenuOpen(false)}>
              <X size={28} />
            </button>
          </div>
          <nav className="flex flex-col space-y-8 text-center">
            {navLinks.map((link) => (
              <Link 
                key={link.name} 
                to={link.path} 
                onClick={() => setIsMobileMenuOpen(false)}
                className="font-serif text-3xl hover:text-brand-gold"
              >
                {link.name}
              </Link>
            ))}
            <Link 
              to="/admin" 
              onClick={() => setIsMobileMenuOpen(false)} 
              className="text-[11px] uppercase tracking-widest text-gray-400 font-bold"
            >
              Espace Gestion
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
